import {useCallback, useState} from "react";
import {ContractCancellationApi} from "./ContractCancellationApi";
import {
	type ContractCancellationDraft,
	type ContractCancellationErrors,
	type ContractCancellationField,
	emptyCancellationDraft,
	isSubmittable,
	requestBodyOf,
	validateContractCancellation,
} from "./contractCancellation";
import {type CancellationFailureKey, cancellationFailureKey} from "./contractCancellationFailure";

/**
 * The React adapter for the § 312k cancellation form (kitecorp/transferify-ui#2327).
 *
 * <p>A thin adapter and nothing else: it holds the draft, calls `contractCancellation` for every rule and
 * `ContractCancellationApi` for the one request, and exposes the result. No rule lives here — the field set,
 * the validation, the request shape and the receipt formatting are all in the React-free module, which is why
 * they have tests and this does not need them.
 *
 * <p>Field errors appear only AFTER a submit attempt, not while somebody is still typing their address. That
 * is the ordinary form courtesy, and it matters more than usual here: the reader is exercising a statutory
 * right, and a page that scolds them mid-keystroke reads as an obstacle, which is the one thing § 312k
 * forbids this page from being.
 */
export interface UseContractCancellation {
	draft: ContractCancellationDraft;
	/** Set one field. Clears that field's own error, so a correction is acknowledged immediately. */
	setField: (field: ContractCancellationField, value: string) => void;
	/** Empty until the first submit attempt; then every independent problem at once. */
	errors: ContractCancellationErrors;
	submitting: boolean;
	/** A key of `cancellation.errors`, or `null`. Set only by a failed request, never by validation. */
	failure: CancellationFailureKey | null;
	/** The api's own receipt instant, ISO-8601 UTC. Non-null exactly when the declaration was accepted. */
	receivedAt: string | null;
	/** What was actually SENT, kept so the confirmation view restates the declaration rather than the form. */
	declared: ContractCancellationDraft | null;
	submit: () => Promise<void>;
}

export function useContractCancellation(): UseContractCancellation {
	const [draft, setDraft] = useState<ContractCancellationDraft>(emptyCancellationDraft);
	const [errors, setErrors] = useState<ContractCancellationErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [failure, setFailure] = useState<CancellationFailureKey | null>(null);
	const [receivedAt, setReceivedAt] = useState<string | null>(null);
	const [declared, setDeclared] = useState<ContractCancellationDraft | null>(null);

	const setField = useCallback((field: ContractCancellationField, value: string) => {
		setDraft(current => ({...current, [field]: value}));
		setErrors(current => {
			if (current[field] === undefined) {
				return current;
			}
			const next = {...current};
			delete next[field];
			return next;
		});
	}, []);

	const submit = useCallback(async () => {
		setFailure(null);
		const found = validateContractCancellation(draft);
		setErrors(found);
		if (!isSubmittable(draft)) {
			return;
		}
		setSubmitting(true);
		try {
			const receipt = await ContractCancellationApi.declare(requestBodyOf(draft));
			// The draft is snapshotted at the moment it was accepted. The confirmation view is EVIDENCE of
			// what was declared (§ 312k Abs. 3), so it must not re-read a live form somebody could edit
			// afterwards and then print as their proof.
			setDeclared(draft);
			setReceivedAt(receipt.receivedAt);
		}
		catch (error) {
			setFailure(cancellationFailureKey(error));
		}
		finally {
			setSubmitting(false);
		}
	}, [draft]);

	return {draft, setField, errors, submitting, failure, receivedAt, declared, submit};
}
